import { message } from 'antd';
import { useCallback, useState } from 'react';

import { startTranscriptAnalysis, updateAssessment } from '@/api/assessments/api';
import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { useAnalysisSkillOptions } from '@/hooks/useAnalysisSkillOptions';
import { useAssessmentData } from '@/hooks/useAssessmentData';
import { type CardPreflightResult, useStep2Preflight } from '@/hooks/useStep2Preflight';
import {
  analysisOptionsToDTO,
  type AnalysisOptions,
  type AssetBucket,
  type Step1DocCard,
} from '@/models/assessment.model';

export interface AnalysisSkillOption {
  value: string;
  label: string;
}

export interface UseTranscriptAnalysisOptions {
  assessmentInstanceId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  onRunAnalysis?: () => void;
  onNavigateAssessmentStep: (step: AssessmentFlowStep) => void;
  onWorkflowStart?: (
    runId: string,
    streamUrl: string,
    counts: { transcriptCount: number; documentCount: number },
  ) => void;
}

export interface UseTranscriptAnalysisResult {
  isLoading: boolean;
  isSaving: boolean;
  isStep1Approved: boolean;
  transcriptCardIds: string[];
  clientDocCardIds: string[];
  analysisOptions: AnalysisOptions;
  engagementContext: string | null;
  stakeholderListMarkdown: string | null;
  step1DocCards: Step1DocCard[];
  allLinkedIds: string[];
  canStartAnalysis: boolean;
  disabledReason: string | null;
  isPreflightValidating: boolean;
  transcriptResults: CardPreflightResult[];
  clientDocResults: CardPreflightResult[];
  analysisSkillOptions: AnalysisSkillOption[];
  selectedAnalysisSkillValue: string | null;
  skillsLoading: boolean;
  transcriptResetKey: number;
  clientDocResetKey: number;
  contextModalCardId: string | null;
  contextModalBucket: AssetBucket | null;
  createNoteModalId: string | null;
  createNoteModalBucket: AssetBucket | null;
  browseDrawerBucket: AssetBucket | null;
  setAnalysisOption: <K extends keyof AnalysisOptions>(key: K, value: AnalysisOptions[K]) => void;
  setSelectedAnalysisSkillValue: (v: string | null) => void;
  openContextModal: (cardId: string, bucket: AssetBucket | null) => void;
  closeContextModal: () => void;
  handleContextModalLink: (cardId: string) => Promise<void>;
  openCreateNoteModal: (id: string, bucket: AssetBucket) => void;
  closeCreateNoteModal: () => void;
  handleCreateNoteSaved: (cardId: string) => void;
  openBrowseDrawer: (bucket: AssetBucket) => void;
  closeBrowseDrawer: () => void;
  selectFromBrowseDrawer: (cardId: string) => void;
  incrementTranscriptReset: () => void;
  incrementClientDocReset: () => void;
  unlinkTranscript: (cardId: string) => Promise<void>;
  unlinkClientDoc: (cardId: string) => Promise<void>;
  startAnalysis: () => Promise<void>;
}

/**
 * Composition layer: wires together assessment data, skill options, preflight, and modal state
 * into the public interface consumed by Step 2 of the assessment flow.
 *
 * Card-linking, modal, and browse-drawer state remain here because the handlers that control
 * modals simultaneously mutate card ID arrays and call persistCardIds — splitting them would
 * require complex state-passing between hooks with no clean ownership boundary.
 */
export function useTranscriptAnalysis({
  assessmentInstanceId,
  accessToken,
  clientId,
  projectId,
  onRunAnalysis,
  onNavigateAssessmentStep,
  onWorkflowStart,
}: UseTranscriptAnalysisOptions): UseTranscriptAnalysisResult {
  const [isSaving, setIsSaving] = useState(false);
  const [contextModalCardId, setContextModalCardId] = useState<string | null>(null);
  const [contextModalBucket, setContextModalBucket] = useState<AssetBucket | null>(null);
  const [createNoteModalId, setCreateNoteModalId] = useState<string | null>(null);
  const [createNoteModalBucket, setCreateNoteModalBucket] = useState<AssetBucket | null>(null);
  const [browseDrawerBucket, setBrowseDrawerBucket] = useState<AssetBucket | null>(null);
  const [transcriptResetKey, setTranscriptResetKey] = useState(0);
  const [clientDocResetKey, setClientDocResetKey] = useState(0);

  const {
    isLoading,
    isStep1Approved,
    transcriptCardIds,
    clientDocCardIds,
    setTranscriptCardIds,
    setClientDocCardIds,
    analysisOptions,
    setAnalysisOptions,
    engagementContext,
    stakeholderListMarkdown,
    step1DocCards,
  } = useAssessmentData({ assessmentInstanceId, accessToken, clientId, projectId });

  const {
    analysisSkillOptions,
    selectedAnalysisSkillValue,
    skillsLoading,
    bundledOption,
    setSelectedAnalysisSkillValue,
  } = useAnalysisSkillOptions({ assessmentInstanceId, accessToken });

  const preflight = useStep2Preflight({
    assessmentId: assessmentInstanceId,
    accessToken,
    clientId,
    projectId,
    transcriptCardIds,
    clientDocCardIds,
  });

  const persistCardIds = useCallback(
    async (transcriptIds: string[], clientDocIds: string[]): Promise<void> => {
      if (!accessToken) {
        return;
      }
      await updateAssessment(
        assessmentInstanceId,
        {
          interview_transcript_card_ids: transcriptIds,
          client_document_card_ids: clientDocIds,
        },
        accessToken,
        clientId,
        projectId,
      );
    },
    [accessToken, assessmentInstanceId, clientId, projectId],
  );

  const setAnalysisOption = useCallback(
    <K extends keyof AnalysisOptions>(key: K, value: AnalysisOptions[K]) => {
      setAnalysisOptions((prev) => ({ ...prev, [key]: value }));
    },
    [setAnalysisOptions],
  );

  const openContextModal = useCallback((cardId: string, bucket: AssetBucket | null) => {
    setContextModalCardId(cardId);
    setContextModalBucket(bucket);
  }, []);

  const closeContextModal = useCallback(() => {
    setContextModalCardId(null);
    setContextModalBucket(null);
  }, []);

  const openCreateNoteModal = useCallback((id: string, bucket: AssetBucket) => {
    setCreateNoteModalId(id);
    setCreateNoteModalBucket(bucket);
  }, []);

  const closeCreateNoteModal = useCallback(() => {
    setCreateNoteModalId(null);
    setCreateNoteModalBucket(null);
  }, []);

  const handleCreateNoteSaved = useCallback(
    (cardId: string): void => {
      const bucket = createNoteModalBucket;
      setCreateNoteModalId(null);
      setCreateNoteModalBucket(null);

      if (bucket === 'transcript') {
        const next = transcriptCardIds.includes(cardId)
          ? transcriptCardIds
          : [...transcriptCardIds, cardId];
        setTranscriptCardIds(next);
        setTranscriptResetKey((k) => k + 1);
        void persistCardIds(next, clientDocCardIds).catch(() => {
          void message.error('Failed to link transcript note.');
        });
      } else if (bucket === 'client-doc') {
        const next = clientDocCardIds.includes(cardId)
          ? clientDocCardIds
          : [...clientDocCardIds, cardId];
        setClientDocCardIds(next);
        setClientDocResetKey((k) => k + 1);
        void persistCardIds(transcriptCardIds, next).catch(() => {
          void message.error('Failed to link document note.');
        });
      }
    },
    [
      createNoteModalBucket,
      transcriptCardIds,
      clientDocCardIds,
      persistCardIds,
      setTranscriptCardIds,
      setClientDocCardIds,
    ],
  );

  const handleContextModalLink = useCallback(
    async (cardId: string): Promise<void> => {
      const bucket = contextModalBucket;
      setContextModalCardId(null);
      setContextModalBucket(null);

      if (bucket === 'transcript') {
        const next = transcriptCardIds.includes(cardId)
          ? transcriptCardIds
          : [...transcriptCardIds, cardId];
        setTranscriptCardIds(next);
        try {
          await persistCardIds(next, clientDocCardIds);
        } catch {
          void message.error('Failed to save transcript link.');
        }
      } else if (bucket === 'client-doc') {
        const next = clientDocCardIds.includes(cardId)
          ? clientDocCardIds
          : [...clientDocCardIds, cardId];
        setClientDocCardIds(next);
        try {
          await persistCardIds(transcriptCardIds, next);
        } catch {
          void message.error('Failed to save document link.');
        }
      }
    },
    [
      contextModalBucket,
      transcriptCardIds,
      clientDocCardIds,
      persistCardIds,
      setTranscriptCardIds,
      setClientDocCardIds,
    ],
  );

  const openBrowseDrawer = useCallback((bucket: AssetBucket) => {
    setBrowseDrawerBucket(bucket);
  }, []);

  const closeBrowseDrawer = useCallback(() => {
    setBrowseDrawerBucket(null);
  }, []);

  const selectFromBrowseDrawer = useCallback(
    (cardId: string) => {
      const bucket = browseDrawerBucket;
      setBrowseDrawerBucket(null);
      if (bucket) {
        setContextModalCardId(cardId);
        setContextModalBucket(bucket);
      }
    },
    [browseDrawerBucket],
  );

  const unlinkTranscript = useCallback(
    async (cardId: string): Promise<void> => {
      const next = transcriptCardIds.filter((id) => id !== cardId);
      setTranscriptCardIds(next);
      try {
        await persistCardIds(next, clientDocCardIds);
      } catch {
        void message.error('Failed to unlink transcript.');
      }
    },
    [transcriptCardIds, clientDocCardIds, persistCardIds, setTranscriptCardIds],
  );

  const unlinkClientDoc = useCallback(
    async (cardId: string): Promise<void> => {
      const next = clientDocCardIds.filter((id) => id !== cardId);
      setClientDocCardIds(next);
      try {
        await persistCardIds(transcriptCardIds, next);
      } catch {
        void message.error('Failed to unlink document.');
      }
    },
    [transcriptCardIds, clientDocCardIds, persistCardIds, setClientDocCardIds],
  );

  const startAnalysis = useCallback(async (): Promise<void> => {
    if (!accessToken) {
      return;
    }
    setIsSaving(true);
    try {
      // Stale-skill guard: if selection no longer exists in loaded options, fall back to bundled
      if (
        selectedAnalysisSkillValue !== null &&
        analysisSkillOptions.length > 0 &&
        !analysisSkillOptions.some((o) => o.value === selectedAnalysisSkillValue)
      ) {
        setSelectedAnalysisSkillValue(bundledOption.value);
      }

      await preflight.runPreflight();

      await updateAssessment(
        assessmentInstanceId,
        {
          interview_transcript_card_ids: transcriptCardIds,
          client_document_card_ids: clientDocCardIds,
          analysis_options: analysisOptionsToDTO(analysisOptions) as Record<string, unknown>,
        },
        accessToken,
        clientId,
        projectId,
      );

      if (onWorkflowStart) {
        const job = await startTranscriptAnalysis(
          assessmentInstanceId,
          {
            transcript_card_ids: transcriptCardIds,
            client_doc_card_ids: clientDocCardIds,
            analysis_options: analysisOptionsToDTO(analysisOptions) as Record<string, unknown>,
            skill_key: selectedAnalysisSkillValue ?? 'transcript-analysis',
          },
          accessToken,
          clientId,
          projectId,
        );
        onWorkflowStart(job.job_id, job.job_stream_url, {
          transcriptCount: transcriptCardIds.length,
          documentCount: clientDocCardIds.length,
        });
      } else if (onRunAnalysis) {
        onRunAnalysis();
      } else {
        onNavigateAssessmentStep('2-review');
      }
    } catch {
      void message.error('Failed to save assessment. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [
    accessToken,
    assessmentInstanceId,
    transcriptCardIds,
    clientDocCardIds,
    analysisOptions,
    clientId,
    projectId,
    onRunAnalysis,
    onNavigateAssessmentStep,
    onWorkflowStart,
    preflight,
    selectedAnalysisSkillValue,
    analysisSkillOptions,
    bundledOption.value,
    setSelectedAnalysisSkillValue,
  ]);

  const allLinkedIds = [...transcriptCardIds, ...clientDocCardIds];
  const modalOpen = contextModalCardId !== null || createNoteModalId !== null;
  const canStartAnalysis =
    isStep1Approved &&
    preflight.transcriptGatePassed &&
    !preflight.isValidating &&
    !skillsLoading &&
    selectedAnalysisSkillValue !== null &&
    !modalOpen &&
    !isSaving;

  const disabledReason: string | null = canStartAnalysis
    ? null
    : !isStep1Approved
      ? 'Complete and approve Step 1 before starting analysis.'
      : !preflight.transcriptGatePassed
        ? 'Link at least one interview transcript to continue.'
        : preflight.isValidating
          ? 'Validating documents…'
          : skillsLoading
            ? 'Loading analysis skill options…'
            : selectedAnalysisSkillValue === null
              ? 'Select an analysis skill to continue.'
              : modalOpen
                ? 'Close the open dialog before starting.'
                : isSaving
                  ? 'Saving your changes…'
                  : null;

  return {
    isLoading,
    isSaving,
    isStep1Approved,
    transcriptCardIds,
    clientDocCardIds,
    analysisOptions,
    engagementContext,
    stakeholderListMarkdown,
    step1DocCards,
    allLinkedIds,
    canStartAnalysis,
    disabledReason,
    isPreflightValidating: preflight.isValidating,
    transcriptResults: preflight.transcriptResults,
    clientDocResults: preflight.clientDocResults,
    analysisSkillOptions,
    selectedAnalysisSkillValue,
    skillsLoading,
    transcriptResetKey,
    clientDocResetKey,
    contextModalCardId,
    contextModalBucket,
    createNoteModalId,
    createNoteModalBucket,
    browseDrawerBucket,
    setAnalysisOption,
    setSelectedAnalysisSkillValue,
    openContextModal,
    closeContextModal,
    handleContextModalLink,
    openCreateNoteModal,
    closeCreateNoteModal,
    handleCreateNoteSaved,
    openBrowseDrawer,
    closeBrowseDrawer,
    selectFromBrowseDrawer,
    incrementTranscriptReset: () => setTranscriptResetKey((k) => k + 1),
    incrementClientDocReset: () => setClientDocResetKey((k) => k + 1),
    unlinkTranscript,
    unlinkClientDoc,
    startAnalysis,
  };
}
